import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import {
  GatewayAliasesEditComponent
} from '@components/administration/gateway/gateway-configuration/gateway-connections/gateway-aliases/gateway-aliases-edit/gateway-aliases-edit.component';
import { DATA_TYPE } from '@constants/data-type.constant';
import { GATEWAY_TYPE } from '@constants/gateway/gateway-type';
import { BIGQUERY_VALID_NAME_REGEX } from '@constants/reg-exp';
import { EnvironmentConfig } from '@environments/environment';
import { Gateway } from '@models/adminstration/gateway/gateway';
import { GatewaySignals } from '@models/adminstration/gateway/gateway-configuration/gateway-signals';
import { GatewayValues } from '@models/adminstration/gateway/gateway-configuration/gateway-values';
import { PaginateControlSettings } from '@models/paginate-control-settings';
import { TranslateService } from '@ngx-translate/core';
import { GatewayService } from '@services/administration/gateway/gateway.service';
import { GatewayImportExportService } from '@services/administration/gateway/import-export/gateway-import-export.service';
import { SortingService } from '@services/administration/sorting/sorting.service';
import { ConfirmationDialogService } from '@services/confirmation-dialog.service';
import { LoadingIndicatorService } from '@services/loading-indicator/loading-indicator.service';
import { ModalService } from '@services/modal/modal.service';
import { NotificationService } from '@services/notification/notification.service';
import { StorageService } from '@services/storage/storage.service';
import { TableHeaderSortableService } from '@services/table-header-sortable/table-header-sortable.service';
import { ConfirmationDialogComponent } from '@shared/confirmation-dialog/confirmation-dialog.component';
import { SortEvent, TableHeaderSortableDirective } from '@shared/table-header-sortable/table-header-sortable.directive';
import * as JSLZString from 'lz-string';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ScrollService } from 'src/app/shared/scroll-service/scroll.service';

@Component({
    selector: 'scitis-admin-gateway-aliases',
    templateUrl: './gateway-aliases.component.html',
    styleUrls: ['./gateway-aliases.component.scss'],
    standalone: false
})
export class GatewayAliasesComponent implements OnInit, OnDestroy {

  @ViewChildren(TableHeaderSortableDirective) sortableHeaders: QueryList<TableHeaderSortableDirective> = new QueryList<TableHeaderSortableDirective>();

  @Input()
  gateway: Gateway = new Gateway();

  @Input()
  gatewaySignals: GatewaySignals[] = new Array<GatewaySignals>();

  @Input()
  isEditable: boolean = false;

  @Input()
  isArgosGateway: boolean = false;

  @Input()
  isGeneric: boolean = false;

  @Input()
  isEavViewFeatureEnabled: boolean = false;

  @Input()
  isStreamingEventDataToPubSubFeatureEnabled: boolean = false;

  @Input()
  superOrTechAdminRole: boolean = false;

  @Input()
  isAdminOrHigher: boolean = false;

  @Output() updateSignals = new EventEmitter<boolean>();

  @ViewChild('resultButtons') resultButtons: ElementRef | undefined;

  private isActive: boolean = true;
  private numberOfClusters: number = 0;
  private COMPONENT_NAME = 'GatewayAliasesComponent';
  private GATEWAY_SIGNALS_SNAPSHOT = 'GATEWAY_SIGNALS_SNAPSHOT';

  itemsPerPageOptions: number[] = [5, 10, 20, 50, 100, 500, 1000];
  paginateControlSettings: PaginateControlSettings = new PaginateControlSettings();
  bigTableStreamingFeatureEnabled: boolean = false;
  isCollapsed: boolean[] = [];
  name: string = '';
  jsonType: string = '';
  configToBeUpdated: GatewaySignals = new GatewaySignals();
  duplicatesFound: boolean = false;
  aliasConfigChanged: boolean = false;
  private destroy$: Subject<boolean> = new Subject<boolean>();
  private nameDuplicatesMap = {};
  private aliasDuplicatesMap = {};
  private duplicateSignalNamesFound: boolean = false;
  private duplicateAliasesFound: boolean = false;

  constructor(private translate: TranslateService,
              private confirmationDialogService: ConfirmationDialogService,
              private gatewayService: GatewayService,
              private tableHeaderSortableService: TableHeaderSortableService,
              private loadingIndicator: LoadingIndicatorService,
              private notification: NotificationService,
              private modalService: ModalService,
              private storageService: StorageService,
              private importExportService: GatewayImportExportService,
              private scrollService: ScrollService,
              private sortingService: SortingService) {
    this.bigTableStreamingFeatureEnabled = EnvironmentConfig.PortalSettings.bigTableStreamingEnabled;
  }

  get saveAliasIsDisabled(): boolean {
    return !this.isAdminOrHigher || this.duplicateAliasesFound || this.duplicateSignalNamesFound || !this.aliasConfigChanged;
  }

  get signalDuplicateAlias(): boolean[] {
    return this.gatewaySignals.map(signal => {
      if (!signal || !signal.values) {
        return false;
      }
      return Boolean(signal.values.find(value => value.duplicated && value.active));
    });
  }

  get signalInvalidAlias(): boolean[] {
    return this.gatewaySignals.map(signal => {
      if (!signal || !signal.values) {
        return false;
      }
      return Boolean(signal.values.find(value => value.invalidChars && value.active));
    });
  }

  ngOnInit() {
    for (const element of this.gatewaySignals) {
      this.isCollapsed.push(false);
    }
    this.numberOfClusters = this.countValuesClustered();
    this.paginateControlSettings.itemsPerPage = 20;
    this.checkForDuplicates();
    this.sortConnections();
    this.saveGatewaySignalsSnapshot();
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }


  checkForDuplicates(): void {
    this.nameDuplicatesMap = this.setDuplicatesMap('name');
    this.aliasDuplicatesMap = this.setDuplicatesMap('alias');

    this.duplicateSignalNamesFound = this.duplicatesFoundInMap(this.nameDuplicatesMap, true);
    this.duplicateAliasesFound = this.duplicatesFoundInMap(this.aliasDuplicatesMap, false);
  }

  public saveGatewaySignalsSnapshot(): void {
    this.storageService.writeValueToLocalStorage(this.GATEWAY_SIGNALS_SNAPSHOT, JSLZString.compressToUTF16(JSON.stringify(this.gatewaySignals)));
  }

  public getGatewaySignalsSnapshot(): any {
    const storageData: string | null = this.storageService.readValueFromLocalStorage(this.GATEWAY_SIGNALS_SNAPSHOT);
    if (!storageData) {
      return [];
    }

    const data = JSON.parse(JSLZString.decompressFromUTF16(storageData));
    return data?.length ? data : [];
  }

  onImportConnectorConfig(config: any) {
    this.configToBeUpdated = config;
  }

  onFileSelected(event: Event): void | undefined {
    if (!event.target) {
      return;
    }

    const target = event.target as HTMLInputElement;
    if (!target || !target.files || target.files.length === 0) {
      return;
    }
    this.loadingIndicator.show(this.translate.instant('Loading JSON'));

    const file = target.files[0];
    if (!file) {
      this.loadingIndicator.hide();
      return;
    }

    const fileReader = new FileReader();
    fileReader.readAsText(file, 'UTF-8');
    fileReader.onload = () => {
    let importedConfig;
      try {
        importedConfig = JSON.parse(String(fileReader.result));
      } catch (error: any) {
        this.notification.error(this.translate.instant('The selected file cannot be parsed, maybe invalid JSON.'), error, this.COMPONENT_NAME, true);
        this.loadingIndicator.hide();
        return;
      }

    if (this.validateConnectorConfig(importedConfig, this.configToBeUpdated)) {
      this.parseAndImportConfig(importedConfig);
      this.notification.success(this.translate.instant('successfully imported configuration.'), this.COMPONENT_NAME, true);
      this.aliasConfigChanged = true;
    }
    this.loadingIndicator.hide();
    };
    fileReader.onerror = (error) => {
      this.notification.error(this.translate.instant('The selected file cannot be parsed, maybe invalid JSON.'), error, this.COMPONENT_NAME, true);
      this.loadingIndicator.hide();
    };

  }

  onColumnSort({column, direction}: SortEvent, config: GatewaySignals) {
    this.sortableHeaders = this.tableHeaderSortableService.resetHeaders(this.sortableHeaders, column);
    this.tableHeaderSortableService.sortItems(config.values, column, direction);
  }

  private parseAndImportConfig(newConfig: any): void {
    const existentConfig = this.getConfigToUpdateByTypeOfData(newConfig.typeOfData);
    if (existentConfig) {
      existentConfig.alias = newConfig.alias;
      existentConfig.values = this.parseExistentSignalsAndUpdate(existentConfig.values, newConfig.values);
    }

  }

  private getConfigToUpdateByTypeOfData(typeOfData: string) {
    for (const config of this.gatewaySignals) {
      if (config.typeOfData === typeOfData) {
        return config;
      }
    }
    return null;
  }

  private parseExistentSignalsAndUpdate(oldValues, newValues) {
    for (const val of oldValues) {
      const newSignal = this.findSignalByName(newValues, val.name);
      if (newSignal) {
        val.description = newSignal.description;
        val.datatype = newSignal.datatype;
        val.dimension = newSignal.dimension;
        val.index = newSignal.index;
        val.alias = newSignal.alias;
        val.active = newSignal.active;
      }
    }
    return oldValues;
  }

  private findSignalByName(newValues, name): any | null {
    for (const val of newValues) {
      if (val.name === name) {
        return val;
      }
    }
    return null;
  }

  private validateConnectorConfig(newConfig: any, existentConfig: GatewaySignals): boolean {
    if (!newConfig) {
      this.notification.error(this.translate.instant('The connector is undefined'), null, this.COMPONENT_NAME, true);
      return false;
    }

    if (!newConfig.typeOfData || newConfig.typeOfData.trim().length === 0) {
      this.notification.error(this.translate.instant('The connector data type is undefined'), null, this.COMPONENT_NAME, true);
      return false;
    }
    if (newConfig.typeOfData !== existentConfig.typeOfData) {
      const message = this.translate.instant('The imported configuration type of data ') + newConfig.typeOfData + this.translate.instant(' is different than the required one ') + existentConfig.typeOfData;
      this.notification.error(message, null, this.COMPONENT_NAME, true);
      return false;
    }
    if (!newConfig.values || newConfig.values.length === 0) {
      this.notification.error(this.translate.instant('The connector has no signals defined'), null, this.COMPONENT_NAME, true);
      return false;
    }
    newConfig.values.forEach(element => {
      element.alias = typeof element.alias === 'string' ? element.alias.trim() : '';
      element.name = typeof element.name === 'string' ? element.name.trim() : '';
    });
    const duplicateSignalsOrAliases = this.getSignalDuplicates(newConfig);
    if (duplicateSignalsOrAliases && duplicateSignalsOrAliases.length > 0) {
      this.notification.error(this.translate.instant('The connector has duplicated signals or aliases'), null, this.COMPONENT_NAME, true);
      const modalRef = this.modalService.open(ConfirmationDialogComponent);
      modalRef.componentInstance.title = 'Error';
      modalRef.componentInstance.body = duplicateSignalsOrAliases;
      modalRef.componentInstance.cancelButtonText = null;
      modalRef.componentInstance.okButtonText = 'OK';

      return false;
    }
    return true;
  }

  private getSignalDuplicates(config: any): string {
    const signalDuplicates = this.importExportService.getDuplicatesForConnectorConfig(config, 'name');
    const aliasDuplicates = this.importExportService.getDuplicatesForConnectorConfig(config, 'alias');
    const indexDuplicates = this.importExportService.getDuplicatesForConnectorConfig(config, 'index');

    let result = '';
    const duplicatedSignals = this.importExportService.generateDuplicatesValueEnumeration(signalDuplicates);
    if (duplicatedSignals && duplicatedSignals.length > 0) {
      result += this.translate.instant('The following signals are duplicated').toBold() + ': <br/>' + duplicatedSignals + '</br>';
    }

    const duplicatedAliases = this.importExportService.generateDuplicatesValueEnumeration(aliasDuplicates);
    if (duplicatedAliases && duplicatedAliases.length > 0) {
      result += this.translate.instant('The following aliases are duplicated').toBold() + ': <br/>' + duplicatedAliases + '</br>';
    }

    const duplicatedIndexes = this.importExportService.generateDuplicatesValueEnumeration(indexDuplicates);
    if (duplicatedIndexes && duplicatedIndexes.length > 0) {
      result += this.translate.instant('The following indexes are duplicated').toBold() + ': <br/>' + duplicatedIndexes + '</br>';
    }
    return result.trim();
  }

  onDeleteAlias(alias: any) {
    const type = this.gateway.cloudplugType;
    const id = this.gateway.id;
    const removeMessageTitle = this.translate.instant('Delete alias');
    const removeMessageBody = this.translate.instant('Do you really want to permanently delete the alias configuration %s ?').replace('%s', alias.typeOfData.toBold());
    const removeMessageOK = this.translate.instant('Delete alias');
    this.confirmationDialogService.handleConfirmationDialog(removeMessageTitle, removeMessageBody, '', removeMessageOK).subscribe({
      next: userConfirmed => {
        if (userConfirmed) {
          this.loadingIndicator.show(this.translate.instant('Deleting...'));
          this.deleteAlias(alias, id, type);
        }
      }, error: (error) => {
        console.log(error);
        this.notification.error(this.translate.instant('Alias could not be deleted'), error, this.COMPONENT_NAME, true);
      }
    });
  }

  openMask(config: GatewaySignals, activeIndex: number): void {
    const modalRef = this.modalService.open(GatewayAliasesEditComponent, {size: 'lg'});
    modalRef.componentInstance.gatewaySignalsConfig = config;
    modalRef.result.then((result) => {
      config.alias = result.alias;
      config.values = result.values;
      this.aliasConfigChanged = true;
      this.scrollToSave();
      this.checkAndRemovesOldDuplicates(config.values);
    });
    this.setCollapsingBehavior(activeIndex, false);
  }

  activateAll(values: any): void | undefined {
    if (!this.isEditable) {
      return;
    }
    for (const val of values) {
      val.active = this.isActive;
      if (this.arrayContainsValue(val.alias, val, values)) {
        val.duplicated = true;
      }
    }
    this.isActive = !this.isActive;
    this.aliasConfigChanged = true;
  }

  onSetInactive(val: any, values: any) {
    if (this.isEditable) {
      val.active = false;
      val.cluster = false;
      this.aliasConfigChanged = true;
    }
  }

  onSetActive(val: GatewayValues, values: GatewayValues[]) {
    if (this.isEditable) {
      val.active = true;
      this.aliasConfigChanged = true;
    }
    if (this.arrayContainsValue(val.alias, val, values)) {
      val.duplicated = true;
    }
  }

  isAllowedValueForClustering(val: any): boolean {
    return val.datatype === DATA_TYPE.STRING || val.datatype === DATA_TYPE.INTEGER || val.datatype === DATA_TYPE.BOOLEAN;
  }

  onDeactivateClusterColumn(val: any) {
    if (!this.isEditable) {
      return;
    }

    val.cluster = false;
    this.numberOfClusters--;
  }

  onActivateClusterColumn(val: any) {
    if (!this.isEditable) {
      return;
    }
    if (this.numberOfClusters === 4) {
      return;
    }
    if (!val.active) {
      return;
    }
    val.cluster = true;
    this.numberOfClusters++;
  }

  handlePageChange(event: any) {
    this.paginateControlSettings.currentPage = event;
  }

  onItemsPerPageChange(itemsPerPage: number): void {
    this.paginateControlSettings = {...this.paginateControlSettings, itemsPerPage, currentPage: 1};
  }

  onEditAliasRow(value: GatewayValues): void {
    if (this.duplicatesFound) {
      return;
    }
    for (const signal of this.gatewaySignals) {
      if (signal.values) {
        for (const val of signal.values) {
          val.aliasEditClass = 'form-inline-edit-inactive';
        }
      }
    }
    if (this.isEditable) {
      value.aliasEditClass = 'form-inline-edit-active';
    }
  }

  onSaveAliasRow(newAlias: any, value: GatewayValues, values: GatewayValues[]) {
    newAlias.value = newAlias.value.trim();

    if (this.verifyAliasInvalidChars(newAlias.value, value)) {
      return;
    }

    if (this.arrayContainsValue(newAlias.value, value, values)) {
      value.duplicated = true;
      value.message = this.translate.instant('The alias "#alias" is already defined. ').replace('#alias', newAlias.value);
      if (!value.active) {
        value.aliasEditClass = 'form-inline-edit-inactive';
        this.duplicatesFound = true;
      }
      value.alias = newAlias.value;
      this.checkAndRemovesOldDuplicates(values);
      return;
    }
    value.alias = newAlias.value;
    this.checkAndRemovesOldDuplicates(values);
    value.duplicated = false;
    value.invalidChars = false;
    this.duplicatesFound = false;
    value.message = '';
    value.aliasEditClass = 'form-inline-edit-inactive';
    this.aliasConfigChanged = true;
  }

  private checkAndRemovesOldDuplicates(values: GatewayValues[]) {
    values.filter(v => v.duplicated).forEach(v => {
      const noDuplicate = values
      .filter(active => active.alias.toUpperCase() === v.alias.toUpperCase())
          .length === 1;
      if (noDuplicate) v.duplicated = false;
    });
  }

  private arrayContainsValue(newAlias: any, value: GatewayValues, values: GatewayValues[]): boolean {
    const index = values.indexOf(value);
    for (let i = 0; i < values.length; i++) {
      const currentValue = values[i].alias;
      if (currentValue && currentValue.toUpperCase() === newAlias.toUpperCase() && i !== index) {
        values[i].duplicated = true;
        return true;
      }
    }
    return false;
  }

  onCancelAliasRow(value: GatewayValues, values: GatewayValues[]) {
    value.aliasEditClass = 'form-inline-edit-inactive';
    this.duplicatesFound = false;
    this.verifyAliasInvalidChars(value.alias, value);
    this.checkAndRemovesOldDuplicates(values);
  }

  initRestoreSavedChanges(signalConfig, activeIndex) {
    const removeMessageTitle = this.translate.instant('Restore alias');
    const removeMessageBody = this.translate.instant('Do you really want to restore the alias configuration?');
    const removeMessageOK = this.translate.instant('Restore alias');
    this.confirmationDialogService.handleConfirmationDialog(removeMessageTitle, removeMessageBody, '', removeMessageOK)
    .subscribe({
      next: userConfirmed => {
        if (userConfirmed) {
          this.cancelSignalAction(signalConfig, activeIndex);
        }
      }, error: (error) => {
        console.log(error);
        this.notification.error(this.translate.instant('Alias could not be deleted'), error, this.COMPONENT_NAME, true);
      }
    });
  }

  cancelSignalAction(signalConfig, activeIndex) {
    if (!this.aliasConfigChanged) {
      this.isCollapsed[activeIndex] = false;
      return;
    }
    const oldSignalsVersion: any = this.getGatewaySignalsSnapshot();
    this.gatewaySignals[activeIndex] = oldSignalsVersion[activeIndex];
    this.aliasConfigChanged = false;
  }

  setCollapsingBehavior(activeIndex: number, isToggle: boolean) {
    this.paginateControlSettings = {
      ...this.paginateControlSettings,
      id: 'pagination' + activeIndex,
      itemsPerPage: 20,
      currentPage: 1
    };

    this.isCollapsed.map((item, i) => {
      if (isToggle) {
        this.isCollapsed[i] = i === activeIndex ? !this.isCollapsed[i] : false;
      } else {
        this.isCollapsed[i] = i === activeIndex;
      }
    });
  }

  private countValuesClustered(): number {
    let count: number = 0;
    for (const signal of this.gatewaySignals) {
      if (signal.values !== null) {

        for (const value of signal.values) {
          if (value.cluster) {
            count++;
          }
        }
      }
    }
    return count;
  }

  private deleteAlias(alias: any, id: string, type: GATEWAY_TYPE) {
    this.gatewayService.deleteAlias(id, alias.id, type).pipe(takeUntil(this.destroy$)).subscribe({
      next: (_success) => {
        this.removeAliasFromConfig(alias);
      }, error: (error) => {
        this.loadingIndicator.hide();
        this.notification.error(this.translate.instant('Error removing alias configuration'), error, this.COMPONENT_NAME, true);
      }
    });
  }

  private removeAliasFromConfig(alias: any) {
    const index = this.gatewaySignals.indexOf(alias);
    if (index >= 0) {
      this.gatewaySignals.splice(index, 1);
    }
    this.loadingIndicator.hide();
    this.notification.success(this.translate.instant('Successfully deleted alias'), this.COMPONENT_NAME, false);
  }

  private setDuplicatesMap(fieldName: string): object {
    const duplicatesMap = {};
    this.gatewaySignals.forEach(gatewaySignal => {
      if (gatewaySignal.values) {
        if (!duplicatesMap[gatewaySignal.id]) {
          duplicatesMap[gatewaySignal.id] = {};
        }

        this.setDuplicateMapForGatewaySignal(gatewaySignal.values, fieldName, gatewaySignal.id, duplicatesMap);
      }
    });
    return duplicatesMap;
  }

  private duplicatesFoundInMap(map, verifyIfActive: boolean): boolean {
    if (!map) {
      return false;
    }

    for (const k in map) {
      const connectorOptions = map[k];
      for (const c in connectorOptions) {
        if (connectorOptions[c].count >= 2 && (!verifyIfActive || connectorOptions[c].active >= 2)) {
          return true;
        }
      }
    }
    return false;
  }

  private setDuplicateMapForGatewaySignal(values: GatewayValues[], fieldName: any, gatewaySignalId: number, duplicatesMap: Record<string, any>) {
    values.forEach(gatewaySignalValue => {
      if (gatewaySignalValue[fieldName] && gatewaySignalValue[fieldName].length > 0) {
        const key = gatewaySignalValue[fieldName].toString().toUpperCase();
        if (!duplicatesMap[gatewaySignalId][key]) {
          duplicatesMap[gatewaySignalId][key] = {};
          duplicatesMap[gatewaySignalId][key].count = 1;
          duplicatesMap[gatewaySignalId][key].active = gatewaySignalValue.active ? 1 : 0;
        } else {
          duplicatesMap[gatewaySignalId][key].count += 1;
          if (gatewaySignalValue.active) {
            duplicatesMap[gatewaySignalId][key].active += 1;
          }
        }
      }
    });
  }

  public onSaveAlias(activeIndex): void {
    this.checkForDuplicates();
    if (this.duplicateSignalNamesFound || this.duplicateAliasesFound) {
      this.notification.error(this.translate.instant('Configuration could not be saved. There are duplicated signals or aliases defined'), null, this.COMPONENT_NAME, true);
    } else {
      this.loadingIndicator.show(this.translate.instant('saving alias configuration'));
      const successMessage = 'Alias configuration saved.';
      const errorMessage = 'Alias configuration could not be saved.';
      this.saveConfiguration(successMessage, errorMessage, [this.gatewaySignals[activeIndex]], this.gateway.id);
    }
  }

  private saveConfiguration(successMessage: string, errorMessage: string, gatewaySignals: GatewaySignals[], gatewayId: string): void {
    this.gatewayService.saveGatewayConnection(gatewayId, gatewaySignals).pipe(takeUntil(this.destroy$)).subscribe({
      next: (success) => {
        this.isCollapsed.fill(false);
        gatewaySignals = success;
        this.notification.success(this.translate.instant(successMessage), this.COMPONENT_NAME, false);
        this.loadingIndicator.hide();
        this.getGatewaySignals();
        this.aliasConfigChanged = false;
      }, error: (error) => {
        const intermediateUpdateError = 'Intermediate configuration update detected. Request denied to prevent lost update.';
        const intermediateUpdateMessage = this.translate.instant('Configuration has been updated by the gateway or another user. Your changes were not saved to prevent data loss. Reload the page to see the latest version. Your edits will be lost.');
        const err = error.error === intermediateUpdateError ? intermediateUpdateMessage : errorMessage;
        this.notification.error(err, error, this.COMPONENT_NAME, true, '', false);
        this.loadingIndicator.hide();
        this.aliasConfigChanged = true;
      }
    });
  }


  public scrollToSave(): void {
    const {nativeElement} = this.resultButtons as ElementRef;
    const positionY = nativeElement ? nativeElement.offsetTop : 0;
    this.scrollService.changePosition(positionY);
  }

  private getGatewaySignals() {
    this.gatewayService.getGatewaySignals(this.gateway.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (signals: any[]) => {
        if (signals?.length) {
          this.gatewaySignals = signals.map(signal => {
            if (signal.values) {
              signal.values.forEach(val => val.aliasEditClass = 'form-inline-edit-inactive');
            }
            return signal;
          });
        }
        this.sortConnections();
        this.saveGatewaySignalsSnapshot();
      }, error: (error) => {
        this.notification.error(this.translate.instant('Error setting Configurations.'), error, this.COMPONENT_NAME, true);
      }
    });
  }

  private sortConnections() {
    this.sortingService.sortArrayByProperty(this.gatewaySignals, 'typeOfData', true);
  }

  private verifyAliasInvalidChars(newAliasValue: string, value: GatewayValues): boolean {

    const regex: RegExp = new RegExp(BIGQUERY_VALID_NAME_REGEX);
    if (!regex.test(newAliasValue)) {
      value.aliasEditClass = 'form-inline-edit-inactive';
      value.message = this.translate.instant('The alias "#alias" has invalid characters').replace('#alias', newAliasValue);
      value.invalidChars = true;
      return true;
    }
    value.invalidChars = false;
    return false;
  }
}
